import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Circle, CircleDot, XCircle } from 'lucide-react';
import {
  REFERRAL_PIPELINE,
  REFERRAL_STAGE_LABELS,
  type PropertyType,
  type ReferralStage,
} from '@valutten/shared-schemas/dist/partnerNetwork';
import { Skeleton } from '../../../components/common/SkeletonLoader';
import { usePartnerReferralDetail, type ReferralEvent } from '../../../hooks/usePartnerPortal';
import { formatCentsAud, formatReferralDate } from '../../../utils/partnerReferrals';
import { StagePill } from '../ReferralsTable';
import { usePartnerPortalContext } from '../portalContext';

const CARD = 'rounded-xl border border-gray-700 bg-gray-800/40 p-4 sm:p-6';

const PROPERTY_TYPE_LABELS: Record<PropertyType, string> = {
  investment: 'Investment',
  owner_occupied: 'Owner occupied',
  commercial: 'Commercial',
};

export type TimelineState = 'done' | 'current' | 'pending';

export interface TimelineStep {
  stage: ReferralStage;
  state: TimelineState;
  /** When the referral last reached this stage, if the history records it. */
  at: unknown;
}

/**
 * The pipeline as a timeline. A lapsed referral shows the stages it reached
 * (from its history) as done and the rest as pending; a live one marks its
 * stage current and every earlier stage done.
 */
export function buildTimeline(stage: ReferralStage, events: ReadonlyArray<ReferralEvent>): TimelineStep[] {
  const lastAt = (s: ReferralStage) => {
    for (let i = events.length - 1; i >= 0; i -= 1) if (events[i].to === s) return events[i].at;
    return null;
  };
  const reached = new Set<ReferralStage>(['submitted', ...events.map((e) => e.to)]);
  const currentIndex = (REFERRAL_PIPELINE as readonly string[]).indexOf(stage);
  return REFERRAL_PIPELINE.map((s, i) => {
    let state: TimelineState;
    if (stage === 'lapsed') state = reached.has(s) ? 'done' : 'pending';
    else if (i < currentIndex) state = 'done';
    else if (i === currentIndex) state = stage === 'settled' ? 'done' : 'current';
    else state = 'pending';
    return { stage: s, state, at: lastAt(s) };
  });
}

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-400">{label}</p>
    <p className="text-sm text-gray-100">{value ?? '—'}</p>
  </div>
);

const ReferralDetailPage: React.FC = () => {
  const { referralId } = useParams<{ referralId: string }>();
  const { partners, hideFees } = usePartnerPortalContext();
  const { data, loading, error, errorCode } = usePartnerReferralDetail(referralId);

  const back = (
    <Link to="/partner-referrals/clients" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-400 hover:text-gray-200">
      <ArrowLeft className="h-4 w-4" /> Back to My clients
    </Link>
  );

  if (loading) {
    return (
      <div className="space-y-4">
        {back}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <Skeleton className="lg:col-span-7" height="16rem" />
          <Skeleton className="lg:col-span-5" height="16rem" />
        </div>
      </div>
    );
  }
  if (error || !data?.referral) {
    return (
      <div className="space-y-4">
        {back}
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300" role="alert">
          {errorCode === 'not-found' || !data?.referral ? 'That referral is not in your list.' : error}
        </div>
      </div>
    );
  }

  const r = data.referral;
  const events = Array.isArray(data.events) ? data.events : [];
  const timeline = buildTimeline(r.stage, events);
  const partnerName = partners.find((p) => p.partnerId === r.partnerId)?.name ?? r.partnerId;
  const lapsedAt = r.stage === 'lapsed' ? [...events].reverse().find((e) => e.to === 'lapsed')?.at ?? r.stageChangedAt : null;

  return (
    <div className="space-y-4">
      {back}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className={`${CARD} space-y-5 lg:col-span-7`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold text-gray-100">{r.clientName}</h2>
              <p className="text-xs text-gray-400">
                {r.id} · sent {formatReferralDate(r.createdAt)}
              </p>
            </div>
            <StagePill stage={r.stage} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Partner" value={partnerName} />
            {r.referringBrokerName && <Field label="Referring broker" value={r.referringBrokerName} />}
            <Field label="Property type" value={r.propertyType ? PROPERTY_TYPE_LABELS[r.propertyType] : null} />
            <Field label="Budget" value={typeof r.budgetCents === 'number' ? formatCentsAud(r.budgetCents) : null} />
            <Field label="Goal" value={r.goal || null} />
            <Field label="Where they are looking" value={r.market || null} />
            <Field label="Finance position" value={r.financeStatus || null} />
            {typeof r.purchasePriceCents === 'number' && (
              <Field label="Purchase price" value={formatCentsAud(r.purchasePriceCents)} />
            )}
          </div>

          {r.notes && (
            <div>
              <p className="text-xs text-gray-400">Your note to {partnerName}</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-gray-200">{r.notes}</p>
            </div>
          )}

          {!hideFees && (
            <div className="flex items-baseline justify-between gap-3 rounded-lg bg-gray-900/60 px-4 py-3">
              <span className="text-sm text-gray-300">Fee on settlement (inc GST)</span>
              <span className="text-sm font-semibold text-gray-100" data-testid="detail-fee">
                {typeof r.referralFeeIncGstCents === 'number'
                  ? formatCentsAud(r.referralFeeIncGstCents)
                  : r.stage === 'lapsed'
                    ? '—'
                    : 'Priced at contract'}
              </span>
            </div>
          )}
        </div>

        <div className={`${CARD} space-y-4 lg:col-span-5`}>
          <h2 className="text-sm font-semibold text-gray-200">Progress</h2>
          {r.stage === 'lapsed' && (
            <div className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              <XCircle className="h-4 w-4 flex-shrink-0" />
              <span>Lapsed{lapsedAt ? ` on ${formatReferralDate(lapsedAt)}` : ''}</span>
            </div>
          )}
          <ol className="space-y-3" aria-label="Referral stages">
            {timeline.map((t) => (
              <li key={t.stage} className="flex items-start gap-3" data-state={t.state}>
                {t.state === 'done' ? (
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-400" />
                ) : t.state === 'current' ? (
                  <CircleDot className="h-5 w-5 flex-shrink-0 text-blue-400" />
                ) : (
                  <Circle className="h-5 w-5 flex-shrink-0 text-gray-600" />
                )}
                <div>
                  <p className={`text-sm font-medium ${t.state === 'pending' ? 'text-gray-500' : 'text-gray-100'}`}>
                    {REFERRAL_STAGE_LABELS[t.stage]}
                  </p>
                  <p className="text-xs text-gray-400">
                    {t.state === 'current' ? 'Current stage' : t.state === 'done' ? 'Complete' : 'Not yet'}
                    {t.state !== 'pending' && formatReferralDate(t.at) !== '—' ? ` · ${formatReferralDate(t.at)}` : ''}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default ReferralDetailPage;
